import { ClockAlert, EyeIcon, ScanSearch, Zap } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useState } from "react";

import { UPCLAIM_ROLES } from "@upclaim/sdk/src/admin-roles";

import { Can } from "@/components/can";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { type ClaimWithRelations, useClaim } from "@/features/claims/api/use-claim";
import { CancelledClaimBanner, ExpirationBanner } from "@/features/claims/components/banners";
import { BlockedFlagBanner } from "@/features/claims/components/blocked-flag-banner";
import { ClaimCircumstances } from "@/features/claims/components/circumstances/claim-circumstances";
import { ClaimDetails } from "@/features/claims/components/claim-details";
import { ClaimGroups } from "@/features/claims/components/claim-groups";
import { ClaimStatePreview } from "@/features/claims/components/claim-state-preview";
import { ClaimTasks } from "@/features/claims/components/claim-tasks";
import { CourtOverview } from "@/features/claims/components/court/court-overview";
import { FlightOverview, type OverviewFlight } from "@/features/claims/components/flight-overview";
import { IncidentViewModal } from "@/features/claims/components/incidents/incident-view-modal";
import { FinancialHealth } from "@/features/claims/components/overview-pending-sections";
import { RealDocumentsRibbon } from "@/features/claims/components/real-documents-ribbon";
import { ForceArchivePanel, NextStepCard } from "@/features/claims/components/state-ctas";
import { getRemainingDelayInDays } from "@/features/claims/lib/dates";
import { useCurrentUser } from "@/hooks/use-current-user";
import { notify } from "@/lib/notify";

interface IncidentSegment {
  value: string;
  label: string;
  icon: ReactNode;
  flight: OverviewFlight;
}

/**
 * Incident segment tabs — port of the legalSpecification.incident block of Overview.vue.
 *
 * The available segments depend on claim data + role gating, so the Tabs must be fully
 * controlled with a stable, memoized trigger list — an uncontrolled Tabs whose TabsTrigger
 * set is rebuilt conditionally on every render defeats Base UI's highlighted-tab tracking
 * and causes a "Maximum update depth exceeded" crash (Sentry ADMIN-DASHBOARD-HX).
 */
function IncidentSegmentsTabs({ claim }: { claim: ClaimWithRelations }) {
  const { can } = useCurrentUser();
  const legalSpecification = claim.legalSpecification;
  const canSeeJurisdictional = can([...UPCLAIM_ROLES, "LAWYER"]);

  const segments = useMemo<IncidentSegment[]>(() => {
    const list: IncidentSegment[] = [];
    if (legalSpecification.faultySegment) {
      list.push({
        value: "faultySegment",
        label: "Vol fautif",
        icon: <ClockAlert className="text-red-700" />,
        flight: legalSpecification.faultySegment.flight as unknown as OverviewFlight,
      });
    }
    if (legalSpecification.impactedSegment) {
      list.push({
        value: "impactedSegment",
        label: "Vol impacté",
        icon: <ScanSearch className="text-indigo-700" />,
        flight: legalSpecification.effectiveFlight,
      });
    }
    if (legalSpecification.jurisdictionalSegment && canSeeJurisdictional) {
      list.push({
        value: "jurisdictionalSegment",
        label: "Vol attaqué",
        icon: <Zap className="text-amber-500" />,
        flight: legalSpecification.jurisdictionalSegment.flight as unknown as OverviewFlight,
      });
    }
    return list;
  }, [legalSpecification, canSeeJurisdictional]);

  const [activeSegment, setActiveSegment] = useState(
    () => segments.find((segment) => segment.value === "impactedSegment")?.value ?? segments[0]?.value,
  );
  const [viewIncidentOpen, setViewIncidentOpen] = useState(false);

  useEffect(() => {
    if (!segments.some((segment) => segment.value === activeSegment)) {
      setActiveSegment(segments[0]?.value);
    }
  }, [segments, activeSegment]);

  if (!activeSegment || !legalSpecification.incident) {
    return null;
  }

  return (
    <div>
      <div className="mt-2 flex justify-end">
        <Button variant="outline" size="sm" onClick={() => setViewIncidentOpen(true)}>
          <EyeIcon />
          Voir l'incident
        </Button>
      </div>
      <IncidentViewModal
        incident={legalSpecification.incident}
        open={viewIncidentOpen}
        onOpenChange={setViewIncidentOpen}
      />
      <Tabs value={activeSegment} onValueChange={(value) => setActiveSegment(value as string)}>
        <TabsList className="mt-2 grid w-full grid-cols-3">
          {segments.map((segment) => (
            <TabsTrigger key={segment.value} value={segment.value} className="gap-2">
              {segment.icon}
              {segment.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {segments.map((segment) => (
          <TabsContent key={segment.value} value={segment.value}>
            <FlightOverview
              claimId={claim.id}
              airline={legalSpecification.effectiveAirline}
              claimTypeName={claim.claimType.name}
              flight={segment.flight}
              reroutingProposals={legalSpecification.impactedSegment.reroutingProposals}
              actualRerouting={legalSpecification.impactedSegment.actualRerouting}
            />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

/** Port of pages/plugin/Overview.vue — full overview content, reused by the
 * claim route and the claims-list slide-over pane. */
export function OverviewTab({ claimId }: { claimId: string }) {
  const { data: claim } = useClaim(claimId);
  const { can } = useCurrentUser();

  const [showBanner, setShowBanner] = useState(true);

  // Review request notification — port of the LEGAL_SUBPOENA_TO_REVIEW watcher.
  useEffect(() => {
    if (!claim || !can(["LAWYER"])) {
      return;
    }
    const claimDocument = claim.claimDocuments.find(
      (d) => d.documentType.type === "LEGAL_SUBPOENA_TO_REVIEW",
    );
    if (
      claimDocument &&
      (claim.claimState.type === "COURT.PROCEDURE_CLASSIQUE_WAIT_FOR_ASSIGNATION_GENERATION" ||
        claim.claimState.type === "COURT.PROCEDURE_CLASSIQUE_WAIT_FOR_GROUP")
    ) {
      // TODO: CTA d'ouverture du document Box dans la notification
      notify.info("Demande de relecture", {
        description: "L'assignation est en attente de votre validation pour envoi aux huissiers",
      });
    }
  }, [claim, can]);

  if (!claim) {
    return (
      <div className="space-y-4 p-4">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  const delayExpiredSince = -getRemainingDelayInDays(
    claim.stateUpdatedAt,
    claim.claimState.expectedDurationInDays as number,
  );

  const stepName = claim.claimLegalStep.name;
  const hasPaymentReceived = stepName === "MONEY_RECEIVED" || stepName === "WAITING_FOR_MONEY";
  const showFinancialHealth =
    stepName === "WAITING_FOR_MONEY" || stepName === "MONEY_RECEIVED" || stepName === "PAYMENT_SENT";

  return (
    <div className="@container text-left">
      <Can permissions={UPCLAIM_ROLES}>
        <BlockedFlagBanner claim={claim} />
      </Can>
      <Can permissions={[...UPCLAIM_ROLES, "LAWYER"]}>
        <RealDocumentsRibbon claim={claim} />
      </Can>
      <ClaimStatePreview claim={claim} />

      <ForceArchivePanel claim={claim} />

      {/* Detected extraordinary circumstances (CX) — priority display for jurists (admin/lawyer) */}
      <div className="my-6">
        <ClaimCircumstances claimId={claim.id} />
      </div>

      {/* Financial Health at top when payment received */}
      {showFinancialHealth && hasPaymentReceived ? (
        <Can permissions={[...UPCLAIM_ROLES, "LAWYER"]}>
          <FinancialHealth claimId={claim.id} claimNumber={claim.claimNumber} />
        </Can>
      ) : null}

      {/* Tasks before the flight: what is due on the claim is read first. */}
      <Can permissions={[...UPCLAIM_ROLES, "LAWYER"]}>
        <ClaimTasks claimId={claim.id} />
      </Can>

      {/* Bento summary: next step + flight/incident glance, side by side above the fold */}
      <div className="my-6 grid grid-cols-1 items-start gap-4 @3xl:grid-cols-5">
        <div className="@3xl:col-span-2">
          {/* @TODO Centralized "switch" component, that would centralize specific action based on state */}
          <NextStepCard claim={claim} />
        </div>
        <div className="@3xl:col-span-3">
          {claim.legalSpecification.incident ? (
            <IncidentSegmentsTabs claim={claim} />
          ) : (
            <FlightOverview
              claimId={claim.id}
              airline={claim.legalSpecification.effectiveAirline}
              claimTypeName={claim.claimType.name}
              flight={claim.legalSpecification.effectiveFlight}
              reroutingProposals={claim.legalSpecification.impactedSegment.reroutingProposals}
              actualRerouting={claim.legalSpecification.impactedSegment.actualRerouting}
            />
          )}
        </div>
      </div>

      <div className="relative">
        <CourtOverview claim={claim} />

        <Can permissions={[...UPCLAIM_ROLES, "LAWYER"]}>
          {/* Awaiting the eligibility check, the admin sees the groups above the study (ClaimValidationCTA). */}
          {claim.claimState.type === "CLAIM_VALIDATION.WAIT_FOR_ELIGIBILITY_CHECK" &&
          can(UPCLAIM_ROLES) ? null : (
            <ClaimGroups claimId={claim.id} />
          )}
        </Can>

        <ClaimDetails claim={claim} />

        {showBanner && delayExpiredSince >= 0 ? (
          <Can permissions={UPCLAIM_ROLES}>
            <ExpirationBanner days={delayExpiredSince} onClose={() => setShowBanner(false)} />
          </Can>
        ) : null}
        {showBanner && claim.claimLegalStep.name === "CANCELLED" ? (
          <CancelledClaimBanner onClose={() => setShowBanner(false)} />
        ) : null}

        {/* Financial Health at bottom when no payment received */}
        {showFinancialHealth && !hasPaymentReceived ? (
          <Can permissions={[...UPCLAIM_ROLES, "LAWYER"]}>
            <FinancialHealth claimId={claim.id} claimNumber={claim.claimNumber} />
          </Can>
        ) : null}
      </div>
    </div>
  );
}
